// src/components/simulation/MobileSecuritySimulation.jsx
import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, Wifi, Battery, Signal, Download, Shield, Camera, Mic, MapPin,
  Users, FileText, BookOpen, RefreshCw, AlertCircle, CheckCircle, XCircle, Info, X
} from 'lucide-react';

const MobileSecuritySimulation = ({ scenarios = [], onComplete, challenge }) => {
  const [currentStep, setCurrentStep] = useState('appStore');
  const [currentPermissionIndex, setCurrentPermissionIndex] = useState(0);
  const [permissionDecisions, setPermissionDecisions] = useState({});
  const [isInstalling, setIsInstalling] = useState(false);
  const [installProgress, setInstallProgress] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [appData, setAppData] = useState(null);
  const [hint, setHint] = useState(null);

  // === Parse challenge data ===
  useEffect(() => {
    if (!scenarios || scenarios.length === 0) {
      setAppData({
        appName: "QR Code Scanner Pro",
        appIcon: "",
        appSize: "18 MB",
        developer: "SecureTools Inc.",
        rating: 4.2,
        downloads: "10M+",
        description: "Fast and secure QR code scanner",
        permissions: [
          { id: "camera", name: "Camera", icon: Camera, description: "Take pictures and record video", reason: "To scan QR codes", necessary: true },
          { id: "storage", name: "Storage", icon: FileText, description: "Read and write files", reason: "Save scanned QR codes", necessary: true },
          { id: "contacts", name: "Contacts", icon: Users, description: "Access your contacts", reason: "Share scan results", necessary: false, suspicious: true },
          { id: "location", name: "Location", icon: MapPin, description: "Access your location", reason: "Add location to scans", necessary: false, suspicious: true },
        ],
        note: "Camera & storage needed. Others are suspicious."
      });
      return;
    }

    const installStep = scenarios.find(s => s.scene === 'install_prompt') || scenarios[0];
    const permissionStep = scenarios.find(s => s.scene === 'permission_decision') || {};

    const necessary = permissionStep.necessary || ['camera', 'storage'];
    const suspicious = permissionStep.optional || ['contacts', 'location', 'microphone'];
    const allIds = [...new Set([...necessary, ...suspicious])];

    const permissionMap = {
      camera: { id: "camera", name: "Camera", icon: Camera, description: "Take pictures and record video", reason: "To scan QR codes", necessary: true },
      storage: { id: "storage", name: "Storage", icon: FileText, description: "Read and write files", reason: "Save scanned QR codes", necessary: true },
      contacts: { id: "contacts", name: "Contacts", icon: Users, description: "Access your contacts", reason: "Share scan results", necessary: false, suspicious: true },
      location: { id: "location", name: "Location", icon: MapPin, description: "Access your location", reason: "Add location to scans", necessary: false, suspicious: true },
      microphone: { id: "microphone", name: "Microphone", icon: Mic, description: "Record audio", reason: "Voice commands", necessary: false, suspicious: true },
    };

    const permissions = allIds.map(id => {
      const base = permissionMap[id] || {
        id,
        name: id.charAt(0).toUpperCase() + id.slice(1),
        icon: Shield,
        description: `Access to ${id}`,
        reason: "App claims it's needed",
        necessary: false,
        suspicious: true
      };
      return { ...base, necessary: necessary.includes(id), suspicious: !necessary.includes(id) };
    });

    setAppData({
      appName: installStep.app || challenge?.title || "QR Code Scanner Pro",
      appIcon: "",
      appSize: installStep.size || "18 MB",
      developer: installStep.developer || "SecureTools Inc.",
      rating: 4.2,
      downloads: "1K+",
      description: installStep.description || "Scan QR codes quickly and securely.",
      permissions,
      note: installStep.note || permissionStep.why || "Think before granting permissions"
    });
  }, [scenarios, challenge]);

  // === Install progress ===
  useEffect(() => {
    if (isInstalling) {
      const timer = setInterval(() => {
        setInstallProgress(prev => {
          if (prev >= 100) {
            clearInterval(timer);
            setIsInstalling(false);
            setCurrentStep('permissions');
            return 100;
          }
          return Math.min(prev + 18, 100);
        });
      }, 200);
      return () => clearInterval(timer);
    }
  }, [isInstalling]);

  const handleInstallClick = () => {
    setIsInstalling(true);
    setInstallProgress(0);
  };

  const handlePermissionDecision = (decision) => {
    const perm = appData.permissions[currentPermissionIndex];
    setPermissionDecisions(prev => ({ ...prev, [perm.id]: decision }));

    const isCorrect = (perm.necessary && decision === 'allow') || (!perm.necessary && decision === 'deny');

    setHint({
      permission: perm.name,
      decision,
      isCorrect,
      message: isCorrect
        ? `Correct! ${perm.name} is ${perm.necessary ? 'required' : 'not needed'} for this app.`
        : `Not ideal. ${perm.name} should be ${perm.necessary ? 'allowed' : 'denied'}.`,
      reason: perm.suspicious
        ? "QR scanners don't need contacts, location, or microphone."
        : "Camera and storage are essential for scanning and saving codes."
    });

    if (currentPermissionIndex < appData.permissions.length - 1) {
      setTimeout(() => setCurrentPermissionIndex(i => i + 1), 800);
    } else {
      setTimeout(() => {
        setShowResults(true);
        onComplete?.({ score: getScore(), decisions: permissionDecisions });
      }, 1200);
    }
  };

  const getScore = () => {
    if (!appData) return 0;
    let correct = 0;
    appData.permissions.forEach(p => {
      const should = p.necessary ? 'allow' : 'deny';
      if (permissionDecisions[p.id] === should) correct++;
    });
    return Math.round((correct / appData.permissions.length) * 100);
  };

  // === Hint Popup (outside phone) ===
  const HintPopup = () => {
    if (!hint) return null;

    const isCorrect = hint.isCorrect;
    const glowColor = isCorrect ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)';
    const borderColor = isCorrect ? 'border-green-400' : 'border-red-400';
    const bgColor = isCorrect ? 'bg-green-50/95' : 'bg-red-50/95';
    const iconColor = isCorrect ? 'text-green-600' : 'text-red-600';
    const titleColor = isCorrect ? 'text-green-800' : 'text-red-800';

    return (
      <div className="fixed top-1/2 right-8 -translate-y-1/2 z-50 perspective-1000">
        <div
          className={`
            relative w-96 p-6 rounded-3xl border-2 backdrop-blur-xl 
            animate-in slide-in-from-right-12 fade-in duration-500 
            ${borderColor} ${bgColor}
          `}
          style={{
            boxShadow: `0 20px 60px -12px ${glowColor}, 0 0 0 1px ${isCorrect ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`
          }}
        >
          {/* Glowing background effect */}
          <div className={`absolute inset-0 rounded-3xl opacity-20 blur-3xl -z-10 ${isCorrect ? 'bg-green-400' : 'bg-red-400'}`} />

          <div className="flex items-start gap-5">
            <div className={`p-3 rounded-2xl shadow-sm ${isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
              {isCorrect ? <CheckCircle className={`w-8 h-8 ${iconColor}`} /> : <XCircle className={`w-8 h-8 ${iconColor}`} />}
            </div>

            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h3 className={`text-xl font-bold ${titleColor} mb-1`}>
                  {isCorrect ? 'Excellent Choice!' : 'Security Alert'}
                </h3>
                <button
                  onClick={() => setHint(null)}
                  className={`-mt-1 -mr-2 p-1 rounded-full hover:bg-black/5 transition-colors ${iconColor}`}
                >
                  <X size={20} />
                </button>
              </div>

              <p className="text-sm font-medium text-gray-800 mb-3 leading-relaxed">
                {hint.message}
              </p>

              <div className="relative overflow-hidden rounded-xl bg-white/60 p-4 text-sm border border-white/50 shadow-inner">
                <div className="flex gap-2 text-gray-700">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-70" />
                  <span>{hint.reason}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // === Results Screen ===
  if (showResults) {
    const score = getScore();
    return (
      <div className="max-w-sm mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden font-sans">
        <div className="bg-black text-white text-xs px-4 py-2 flex justify-between">
          <span>9:41</span>
          <div className="flex items-center gap-2">
            <Signal className="w-4 h-4" />
            <Wifi className="w-4 h-4" />
            <Battery className="w-4 h-4" />
            <span>100%</span>
          </div>
        </div>
        <div className="p-8 text-center">
          <div className={`text-8xl mb-6 ${score >= 80 ? 'text-green-500' : score >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
            {score >= 80 ? 'Shield' : score >= 60 ? 'Warning' : 'Alert'}
          </div>
          <h2 className="text-2xl font-bold mb-4">Final Score</h2>
          <div className="text-5xl font-bold text-blue-600 mb-2">{score}%</div>
          <p className="text-gray-600 mb-8">You made {score >= 80 ? 'excellent' : 'some'} safe choices</p>
          <button
            onClick={() => onComplete?.({ score })}
            className="w-full bg-[#00B0FF] text-white py-5 rounded-2xl font-bold text-lg hover:bg-[#0099e5]"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // === Main UI ===
  if (!appData) return <div className="text-center py-20 text-gray-500">Loading...</div>;

  const currentPermission = appData.permissions[currentPermissionIndex];
  const PermissionIcon = currentPermission?.icon || Shield;

  return (
    <div className="relative min-h-[100dvh] bg-transparent text-black">
      <HintPopup />

      <div className="max-w-sm mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden font-sans">
        {/* Status Bar */}
        <div className="bg-black text-white text-xs px-4 py-2 flex justify-between">
          <span>9:41</span>
          <div className="flex items-center gap-2">
            <Signal className="w-4 h-4" />
            <Wifi className="w-4 h-4" />
            <Battery className="w-4 h-4" />
            <span>100%</span>
          </div>
        </div>

        {currentStep === 'appStore' ? (
          <div>
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center">
              <ChevronLeft className="w-6 h-6 text-gray-700" />
              <div className="flex-1 text-center -ml-6 text-lg font-medium text-gray-900">Google Play</div>
            </div>

            <div className="p-6">
              <div className="flex gap-5 items-start mb-6">
                <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center text-5xl shadow-lg">
                  {appData.appIcon}
                </div>
                <div>
                  <h1 className="text-2xl font-bold">{appData.appName}</h1>
                  <p className="text-sm text-gray-600">{appData.developer}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-green-600 font-bold text-lg">{appData.rating}</span>
                    <span className="text-sm text-gray-600">• {appData.downloads}</span>
                  </div>
                </div>
              </div>

              {appData.note && (
                <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-6 text-sm">
                  <strong>Hint:</strong> {appData.note}
                </div>
              )}

              {isInstalling ? (
                <div className="text-center py-12">
                  <div className="text-4xl font-bold text-[#00B0FF] mb-4">{Math.round(installProgress)}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-[#00B0FF] h-3 rounded-full transition-all duration-300"
                      style={{ width: `${installProgress}%` }}
                    />
                  </div>
                  <p className="text-gray-600 mt-4">Installing {appData.appName}...</p>
                </div>
              ) : (
                <button
                  onClick={handleInstallClick}
                  className="w-full bg-[#00B0FF] text-white py-5 rounded-2xl font-bold text-lg shadow-lg flex items-center justify-center gap-3 hover:bg-[#0099e5] transition"
                >
                  <Download size={28} /> Install
                </button>
              )}
            </div>
          </div>
        ) : (
          /* Permission Screen */
          <div className="p-4 sm:p-6 pt-8 sm:pt-10 min-h-[100dvh] bg-white">
            {currentPermission && (
              <>
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-blue-100 rounded-full mx-auto flex items-center justify-center mb-6">
                    <PermissionIcon className="w-10 h-10 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold mb-3">
                    Allow {appData.appName} to access your {currentPermission.name.toLowerCase()}?
                  </h2>
                  <p className="text-gray-600 text-sm px-6">
                    {currentPermission.description}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-2xl p-5 mb-6">
                  <div className="flex items-start gap-4">
                    <Shield className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <div className="font-medium text-gray-900">
                        "To {currentPermission.reason.toLowerCase()}"
                      </div>
                    </div>
                  </div>
                </div>

                {currentPermission.suspicious && (
                  <div className="bg-red-50 border border-red-300 rounded-2xl p-4 mb-6">
                    <div className="flex items-center gap-3 text-red-700 font-medium">
                      <AlertCircle className="w-6 h-6" />
                      This permission is not needed for a QR scanner
                    </div>
                  </div>
                )}

                <div className="text-center text-sm text-gray-500 mb-8">
                  Permission {currentPermissionIndex + 1} of {appData.permissions.length}
                </div>

                <div className="space-y-4">
                  <button
                    onClick={() => handlePermissionDecision('deny')}
                    className="w-full bg-gray-200 text-gray-800 py-5 rounded-2xl font-medium text-lg hover:bg-gray-300 transition"
                  >
                    Deny
                  </button>
                  <button
                    onClick={() => handlePermissionDecision('allow')}
                    className="w-full bg-[#00B0FF] text-white py-5 rounded-2xl font-bold text-lg hover:bg-[#0099e5] transition shadow-lg"
                  >
                    Allow
                  </button>
                </div>

                <div className="mt-10 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#00B0FF] h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((currentPermissionIndex + 1) / appData.permissions.length) * 100}%` }}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSecuritySimulation;